import { setupAiActions } from './ai';
import {
  checkAuth,
  fetchExampleBlob,
  fetchExamples,
  fetchServerKeys,
  login,
  transcribe,
  uploadVideo,
} from './api';
import { cacheAudio, clearCachedAudio, loadCachedAudio } from './audioCache';
import { consumePendingAfterSettings, setPendingAfterSettings, setupEditMode } from './edit';
import { exportDocx, exportPdf, exportTxt, getFormattedTranscript } from './exports';
import { SUPPORTED_LANGUAGES, getLanguage, setLanguage } from './i18n';
import { destroyPlayer, initPlayer, setupPlayerShortcuts } from './player';
import {
  getDeepgramKey,
  getGeminiKey,
  loadTranscript,
  saveTranscript,
  setDeepgramKey,
  setGeminiKey,
  state,
} from './state';
import { processDeepgramResponse, renderSpeakers, renderTranscript } from './transcript';
import type { Language } from './types';
import { $, $maybe, confirmModal, showToast } from './ui';
import { showView } from './views';

document.addEventListener('DOMContentLoaded', () => {
  wireSettings();
  wireUpload();
  wireExamples();
  wireResults();
  wireReset();
  wireLanguageSelector();
  setupEditMode();
  setupAiActions();
  setupPlayerShortcuts();

  if (loadTranscript()) {
    showView('resultsView');
    renderSpeakers();
    renderTranscript();
    void loadCachedAudio().then((blob) => {
      if (blob) {
        if (state.currentAudioUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentAudioUrl);
        }
        state.currentAudioUrl = URL.createObjectURL(blob);
      }
      initPlayer();
    });
  }
});

function wireSettings(): void {
  const modal = $('settingsModal');
  const settingsBtn = $('settingsBtn');
  const closeBtn = $('closeSettingsBtn');
  const passwordInput = $<HTMLInputElement>('globalPasswordInput');
  const deepgramInput = $<HTMLInputElement>('apiKeyInput');
  const geminiInput = $<HTMLInputElement>('geminiApiKeyInput');
  const saveBtn = $('saveApiBtn');

  void checkAuth().then((info) => {
    if (!info.loginEnabled) {
      const wrapper = passwordInput.closest('div')?.parentElement;
      if (wrapper) wrapper.style.display = 'none';
    }
  });

  const dgKey = getDeepgramKey();
  if (dgKey) deepgramInput.value = dgKey;
  const gmKey = getGeminiKey();
  if (gmKey) geminiInput.value = gmKey;

  settingsBtn.addEventListener('click', () => modal.classList.add('active'));
  closeBtn.addEventListener('click', () => modal.classList.remove('active'));

  saveBtn.addEventListener('click', async () => {
    const pwd = passwordInput.value.trim();
    const dg = deepgramInput.value.trim();
    const gm = geminiInput.value.trim();

    setDeepgramKey(dg);
    setGeminiKey(gm);

    let loggedIn = false;
    if (pwd) {
      const res = await login(pwd);
      if (res.success) {
        loggedIn = true;
        passwordInput.value = '';
        showToast('Contraseña aceptada. Sesión iniciada.', 'success');
      } else {
        showToast(res.error ?? 'Contraseña incorrecta', 'error');
        return;
      }
    }

    if (!loggedIn && !dg) {
      showToast('Introduce la Contraseña Global o una API Key de Deepgram.', 'warning');
      return;
    }

    modal.classList.remove('active');
    const pending = consumePendingAfterSettings();
    if (pending) pending();
  });
}

function wireLanguageSelector(): void {
  const select = $maybe<HTMLSelectElement>('languageSelect');
  if (!select) return;
  for (const lang of SUPPORTED_LANGUAGES) {
    const opt = document.createElement('option');
    opt.value = lang.code;
    opt.textContent = lang.label;
    select.appendChild(opt);
  }
  select.value = getLanguage();
  select.addEventListener('change', () => {
    setLanguage(select.value as Language);
  });
}

function wireUpload(): void {
  const dropZone = $('dropZone');
  const fileInput = $<HTMLInputElement>('fileInput');
  const uploadBtn = $('uploadBtn');

  uploadBtn.addEventListener('click', () => fileInput.click());

  dropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropZone.classList.add('bg-white/60');
  });
  dropZone.addEventListener('dragleave', () => dropZone.classList.remove('bg-white/60'));
  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('bg-white/60');
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  });
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void handleFile(file);
  });
}

function wireExamples(): void {
  const cloudIcon = $maybe('cloudIconWrapper');
  const modal = $maybe('examplesModal');
  const closeBtn = $maybe('closeExamplesBtn');
  if (!cloudIcon || !modal || !closeBtn) return;

  let clickCount = 0;
  let resetTimer: ReturnType<typeof setTimeout> | null = null;

  cloudIcon.addEventListener('click', (e) => {
    e.stopPropagation();
    clickCount += 1;
    if (resetTimer) clearTimeout(resetTimer);
    if (clickCount >= 3) {
      clickCount = 0;
      void openExamplesModal();
    } else {
      resetTimer = setTimeout(() => {
        clickCount = 0;
      }, 600);
    }
  });

  closeBtn.addEventListener('click', () => modal.classList.remove('active'));
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.classList.remove('active');
  });
}

async function openExamplesModal(): Promise<void> {
  const modal = $('examplesModal');
  const list = $('examplesList');
  const empty = $('examplesEmpty');
  const errorBox = $('examplesError');

  list.innerHTML =
    '<div class="text-on-surface-variant text-sm flex items-center gap-2"><span class="material-symbols-outlined animate-spin">progress_activity</span> Cargando ejemplos…</div>';
  empty.style.display = 'none';
  errorBox.style.display = 'none';
  modal.classList.add('active');

  const authOk = await ensureAuthForServerUpload();
  if (!authOk) {
    setPendingAfterSettings(() => void openExamplesModal());
    modal.classList.remove('active');
    return;
  }

  try {
    const files = await fetchExamples();
    list.innerHTML = '';
    if (files.length === 0) {
      empty.style.display = 'block';
      return;
    }
    for (const f of files) {
      const btn = document.createElement('button');
      btn.className =
        'w-full bg-surface-container-low hover:bg-primary/10 border border-outline-variant text-on-surface px-4 py-3 rounded-xl flex items-center gap-3 transition-all text-left';
      btn.innerHTML = `
        <span class="material-symbols-outlined text-primary">audio_file</span>
        <span class="flex-1">
          <span class="block font-bold">${escapeHtml(f.name)}</span>
          <span class="block text-xs text-on-surface-variant">${formatBytes(f.size)}</span>
        </span>
        <span class="material-symbols-outlined text-primary">play_arrow</span>
      `;
      btn.addEventListener('click', () => {
        void pickExample(f.name);
      });
      list.appendChild(btn);
    }
  } catch (err) {
    list.innerHTML = '';
    errorBox.textContent = (err as Error).message;
    errorBox.style.display = 'block';
  }
}

async function pickExample(name: string): Promise<void> {
  const modal = $('examplesModal');
  try {
    const blob = await fetchExampleBlob(name);
    const ext = name.split('.').pop()?.toLowerCase() ?? 'mp3';
    const mime = blob.type || `audio/${ext === 'mp3' ? 'mpeg' : ext}`;
    const file = new File([blob], name, { type: mime });
    modal.classList.remove('active');
    void handleFile(file);
  } catch (err) {
    showToast(`No se pudo cargar el ejemplo: ${(err as Error).message}`, 'error');
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function ensureAuthForServerUpload(): Promise<boolean> {
  const info = await checkAuth();
  if (info.authenticated) return true;
  if (!info.loginEnabled) {
    showToast('El servidor no tiene login configurado. Define APP_PASSWORD en stack.env.', 'error');
    return false;
  }
  setPendingAfterSettings(() => {
    const file = state.currentFile;
    if (file) void handleFile(file);
  });
  $('settingsModal').classList.add('active');
  return false;
}

async function handleFile(file: File): Promise<void> {
  state.currentFile = file;
  showView('extractView');

  const extractActions = $('extractActions');
  const extractLoading = $('extractLoading');
  const extractTitle = $('extractTitle');
  const extractStatus = $('extractStatus');
  const extractProgressWrapper = $('extractProgressWrapper');
  const extractProgressBar = $('extractProgressBar');
  const extractProgressPercent = $('extractProgressPercent');
  const downloadMp3Btn = $<HTMLAnchorElement>('downloadMp3Btn');

  extractActions.style.display = 'none';
  extractLoading.style.display = 'flex';

  if (!file.type.startsWith('video/')) {
    extractTitle.innerText = 'Archivo de Audio Detectado';
    extractStatus.innerText = 'El archivo ya es un audio. No se necesita extracción.';
    extractLoading.style.display = 'none';
    extractProgressWrapper.style.display = 'none';
    state.currentAudioUrl = URL.createObjectURL(file);
    void cacheAudio(file);
    downloadMp3Btn.style.display = 'none';
    extractActions.style.display = 'flex';
    return;
  }

  extractTitle.innerText = 'Extrayendo Audio del Vídeo...';
  extractStatus.innerText = 'Subiendo vídeo al servidor...';
  extractProgressWrapper.style.display = 'none';
  extractProgressBar.style.width = '0%';
  extractProgressPercent.innerText = '0%';

  const authOk = await ensureAuthForServerUpload();
  if (!authOk) return;

  try {
    const uploadRes = await uploadVideo(file);
    if (!uploadRes.success || !uploadRes.fileId) {
      throw new Error(uploadRes.error ?? 'Error al subir');
    }

    extractStatus.innerText = 'Convirtiendo a MP3...';
    extractProgressWrapper.style.display = 'block';

    await new Promise<void>((resolve, reject) => {
      const evt = new EventSource(`/api/extract-progress/${uploadRes.fileId}`);
      evt.onmessage = (ev) => {
        const msg = JSON.parse(ev.data) as
          | { type: 'progress'; percent: number }
          | { type: 'done'; downloadUrl: string; fileName: string }
          | { type: 'error'; message: string };
        if (msg.type === 'progress') {
          const p = Math.min(msg.percent, 100);
          extractProgressBar.style.width = `${p}%`;
          extractProgressPercent.innerText = `${p}%`;
          extractStatus.innerText = p < 50 ? 'Analizando pistas de audio...' : 'Codificando MP3...';
        } else if (msg.type === 'done') {
          evt.close();
          extractProgressBar.style.width = '100%';
          extractProgressPercent.innerText = '100%';
          const originalBase = file.name.replace(/\.[^.]+$/, '') || 'audio';
          const desiredName = `${originalBase}.mp3`;
          const downloadHref = `${msg.downloadUrl}?name=${encodeURIComponent(desiredName)}`;
          state.currentAudioUrl = downloadHref;
          extractLoading.style.display = 'none';
          extractStatus.style.display = 'none';
          downloadMp3Btn.href = downloadHref;
          downloadMp3Btn.style.display = 'flex';
          extractActions.style.display = 'flex';
          void fetch(downloadHref, { credentials: 'include' })
            .then((r) => (r.ok ? r.blob() : null))
            .then((blob) => {
              if (blob) return cacheAudio(blob);
            })
            .catch((err) => console.warn('[audioCache] fetch falló:', err));
          resolve();
        } else if (msg.type === 'error') {
          evt.close();
          reject(new Error(msg.message));
        }
      };
      evt.onerror = () => {
        evt.close();
        reject(new Error('Conexión perdida con el servidor'));
      };
    });
  } catch (err) {
    console.error(err);
    showToast(`Error extrayendo audio: ${(err as Error).message}`, 'error');
    showView('uploadView');
  }
}

function wireResults(): void {
  const startBtn = $('startTranscribeBtn');
  startBtn.addEventListener('click', () => {
    void startTranscription();
  });

  const copyBtn = $('copyBtn');
  copyBtn.addEventListener('click', () => {
    if (state.paragraphs.length === 0) return;
    const includeTs = $<HTMLInputElement>('includeTimestamps').checked;
    const text = getFormattedTranscript(includeTs);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const original = copyBtn.innerHTML;
        copyBtn.innerHTML = `<span class="material-symbols-outlined">check</span> ¡Copiado!`;
        setTimeout(() => {
          copyBtn.innerHTML = original;
        }, 2000);
      })
      .catch(() => showToast('No se pudo copiar al portapapeles.', 'error'));
  });

  $('exportTxtBtn').addEventListener('click', () => {
    const includeTs = $<HTMLInputElement>('includeTimestamps').checked;
    exportTxt(includeTs);
  });
  $('exportPdfBtn').addEventListener('click', () => {
    const includeTs = $<HTMLInputElement>('includeTimestamps').checked;
    exportPdf(includeTs);
  });
  $('exportDocxBtn').addEventListener('click', () => {
    const includeTs = $<HTMLInputElement>('includeTimestamps').checked;
    void exportDocx(includeTs);
  });
}

async function startTranscription(): Promise<void> {
  let dgKey = getDeepgramKey();
  if (!dgKey) {
    const serverKeys = await fetchServerKeys();
    if (serverKeys?.deepgram) {
      dgKey = serverKeys.deepgram;
    } else {
      setPendingAfterSettings(() => void startTranscription());
      $('settingsModal').classList.add('active');
      return;
    }
  }

  showView('loadingView');

  const transcribePhase = $('transcribePhase');
  const transcribeProgressBar = $('transcribeProgressBar');
  const loadingStatus = $('loadingStatus');
  const loadingSubStatus = $('loadingSubStatus');

  loadingStatus.innerText = 'Procesando con IA...';
  loadingSubStatus.innerText = 'Preparando el audio para enviar a la nube.';
  transcribePhase.innerText = 'Preparando...';

  transcribeProgressBar.className = 'progress-bar';
  transcribeProgressBar.style.width = '5%';

  try {
    let audio: Blob | File;
    if (state.currentAudioUrl) {
      transcribePhase.innerText = 'Descargando audio...';
      transcribeProgressBar.style.width = '10%';
      loadingSubStatus.innerText = 'Recuperando el MP3 del servidor local.';
      const res = await fetch(state.currentAudioUrl, { credentials: 'include' });
      audio = await res.blob();
    } else if (state.currentFile) {
      audio = state.currentFile;
    } else {
      throw new Error('No hay audio cargado');
    }

    transcribePhase.innerText = 'Enviando a la nube...';
    transcribeProgressBar.style.width = '25%';
    loadingSubStatus.innerText = 'Subiendo audio a la nube para transcripción.';

    let fakeProgress = 25;
    const interval = setInterval(() => {
      if (fakeProgress < 85) {
        fakeProgress += Math.random() * 3;
        transcribeProgressBar.style.width = `${Math.min(fakeProgress, 85)}%`;
      }
      if (fakeProgress > 30 && fakeProgress < 50) {
        transcribePhase.innerText = 'Analizando audio...';
        loadingSubStatus.innerText = 'La IA está identificando voces y palabras.';
      } else if (fakeProgress >= 50 && fakeProgress < 70) {
        transcribePhase.innerText = 'Transcribiendo...';
        loadingSubStatus.innerText = 'Convirtiendo voz a texto con alta precisión.';
      } else if (fakeProgress >= 70) {
        transcribePhase.innerText = 'Separando interlocutores...';
        loadingSubStatus.innerText = 'Identificando quién dice cada frase.';
      }
    }, 800);

    const data = await transcribe(dgKey, audio, getLanguage());
    clearInterval(interval);

    transcribeProgressBar.style.width = '90%';
    transcribePhase.innerText = 'Procesando resultado...';
    loadingSubStatus.innerText = 'Organizando la transcripción final.';

    processDeepgramResponse(data);

    transcribeProgressBar.style.width = '100%';
    transcribePhase.innerText = '¡Completado!';
    loadingSubStatus.innerText = 'Transcripción lista.';

    await new Promise((r) => setTimeout(r, 400));

    renderSpeakers();
    renderTranscript();
    saveTranscript();
    showView('resultsView');
    initPlayer();
  } catch (err) {
    console.error(err);
    showToast(`Error en la transcripción: ${(err as Error).message}. Inténtalo de nuevo.`, 'error');
    showView('extractView');
  }
}

function wireReset(): void {
  $('resetAppBtn').addEventListener('click', async () => {
    const ok = await confirmModal({
      title: 'Empezar de cero',
      message: '¿Seguro que quieres borrar la transcripción actual y empezar de nuevo?',
      confirmLabel: 'Sí, borrar',
      cancelLabel: 'Cancelar',
      danger: true,
    });
    if (!ok) return;
    if (state.currentAudioUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(state.currentAudioUrl);
    }
    destroyPlayer();
    void clearCachedAudio();
    state.paragraphs = [];
    state.speakerMap = {};
    state.undoStack = [];
    state.selectedBubbles.clear();
    state.currentAudioUrl = null;
    state.currentFile = null;
    saveTranscript();
    showView('uploadView');
    const fileInput = $<HTMLInputElement>('fileInput');
    fileInput.value = '';
  });
}
